import { defineStore } from "pinia";
import {
    completenessSummary,
    isGroupComplete,
    sectionFlagsFromApi,
    sectionFlagsFromGroup,
} from "@/config/sellerProfile/completeness";
import { SELLER_PROFILE_GROUP } from "@/config/sellerProfile/groups";
import { emptyPaymentDelivery } from "@/config/sellerProfile/paymentDeliveryCompleteness";
import { PAYMENT_DELIVERY_SECTIONS } from "@/config/sellerProfile/paymentDeliverySections";
import { emptyStoreInfo } from "@/config/sellerProfile/storeInfoCompleteness";
import { STORE_INFO_SECTIONS } from "@/config/sellerProfile/storeInfoSections";
import { emptyStoreLocations, STORE_LOCATIONS_SECTION_ID } from "@/config/sellerProfile/storeLocationsCompleteness";
import { STORE_LOCATIONS_SECTIONS } from "@/config/sellerProfile/storeLocationsSections";
import {
    createStoreLocation,
    deleteStoreLocation,
    duplicateStoreLocation,
    getCompleteness,
    getPaymentDelivery,
    getStoreInfo,
    getStoreLocations,
    updateSection as persistPaymentDeliverySection,
    updateStoreInfoSection as persistStoreInfoSection,
    updateStoreLocation,
    updateStoreLocationsSection as persistStoreLocationsSection,
} from "@/services/sellerProfile/SellerProfileService";
import { useShopStore } from "@/store/shop";

export const useSellerProfileStore = defineStore("sellerProfile", {
    state: () => ({
        shopId: null,
        loading: false,
        paymentDelivery: emptyPaymentDelivery(),
        paymentDeliveryComplete: false,
        storeInfo: emptyStoreInfo(),
        storeInfoComplete: false,
        storeLocations: emptyStoreLocations(),
        storeLocationsComplete: false,
        sections: {},
        completenessReady: false,
    }),
    getters: {
        completeness(state) {
            return completenessSummary(state.sections);
        },
    },
    actions: {
        resetCompleteness() {
            this.paymentDeliveryComplete = false;
            this.storeInfoComplete = false;
            this.storeLocationsComplete = false;
            this.sections = {};
            this.completenessReady = false;
        },
        patchSectionCompleteness(groupState) {
            this.sections = {
                ...this.sections,
                ...sectionFlagsFromGroup(groupState),
            };
        },
        syncPaymentDeliveryComplete() {
            this.paymentDeliveryComplete = isGroupComplete(this.paymentDelivery, PAYMENT_DELIVERY_SECTIONS);
        },
        syncStoreInfoComplete() {
            this.storeInfoComplete = isGroupComplete(this.storeInfo, STORE_INFO_SECTIONS);
        },
        syncStoreLocationsComplete() {
            this.storeLocationsComplete = isGroupComplete(
                this.storeLocations,
                STORE_LOCATIONS_SECTIONS
            );
        },
        async loadCompleteness(shopId) {
            const nextShopId = shopId || null;

            if (!nextShopId) {
                this.shopId = null;
                this.resetCompleteness();
                return;
            }

            if (this.shopId === nextShopId && this.completenessReady) {
                return;
            }

            this.shopId = nextShopId;
            this.resetCompleteness();

            try {
                const data = await getCompleteness(nextShopId);

                if (this.shopId !== nextShopId) {
                    return;
                }

                this.sections = {
                    ...sectionFlagsFromApi(data.sections),
                    ...this.sections,
                };
                this.paymentDeliveryComplete = Boolean(data.paymentDeliveryComplete);
                this.storeInfoComplete = Boolean(data.storeInfoComplete);
                this.storeLocationsComplete = Boolean(data.storeLocationsComplete);
            } catch {
                if (this.shopId !== nextShopId) {
                    return;
                }

                this.paymentDeliveryComplete = false;
                this.storeInfoComplete = false;
                this.storeLocationsComplete = false;
            } finally {
                if (this.shopId === nextShopId) {
                    this.completenessReady = true;
                }
            }
        },
        async loadPaymentDelivery(shopId) {
            const nextShopId = shopId || null;

            if (this.shopId !== nextShopId) {
                this.resetCompleteness();
            }

            this.shopId = nextShopId;
            this.paymentDelivery = emptyPaymentDelivery();

            if (!nextShopId) {
                this.loading = false;
                return;
            }

            this.loading = true;
            const requestedShopId = nextShopId;

            try {
                const profile = await getPaymentDelivery(requestedShopId);

                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.paymentDelivery = profile;
                this.syncPaymentDeliveryComplete();
                this.patchSectionCompleteness(profile);
                this.completenessReady = true;
            } catch {
                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.paymentDelivery = emptyPaymentDelivery();
                this.syncPaymentDeliveryComplete();
                this.completenessReady = true;
            } finally {
                if (this.shopId === requestedShopId) {
                    this.loading = false;
                }
            }
        },
        async loadStoreInfo(shopId) {
            const nextShopId = shopId || null;

            if (this.shopId !== nextShopId) {
                this.resetCompleteness();
            }

            this.shopId = nextShopId;
            this.storeInfo = emptyStoreInfo();

            if (!nextShopId) {
                this.loading = false;
                return;
            }

            this.loading = true;
            const requestedShopId = nextShopId;

            try {
                const profile = await getStoreInfo(requestedShopId);

                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.storeInfo = profile;
                this.syncStoreInfoComplete();
                this.patchSectionCompleteness(profile);
                this.completenessReady = true;
            } catch {
                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.storeInfo = emptyStoreInfo();
                this.syncStoreInfoComplete();
                this.completenessReady = true;
            } finally {
                if (this.shopId === requestedShopId) {
                    this.loading = false;
                }
            }
        },
        applyStoreLocationsSection(saved) {
            this.storeLocations[STORE_LOCATIONS_SECTION_ID] = saved;
            this.syncStoreLocationsComplete();
            this.patchSectionCompleteness({ [STORE_LOCATIONS_SECTION_ID]: saved });
            this.completenessReady = true;

            return saved;
        },
        async loadStoreLocations(shopId, page = 1) {
            const nextShopId = shopId || null;

            if (this.shopId !== nextShopId) {
                this.resetCompleteness();
            }

            this.shopId = nextShopId;
            this.storeLocations = emptyStoreLocations();

            if (!nextShopId) {
                this.loading = false;
                return;
            }

            this.loading = true;
            const requestedShopId = nextShopId;

            try {
                const profile = await getStoreLocations(requestedShopId, page);

                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.storeLocations = profile;
                this.syncStoreLocationsComplete();
                this.patchSectionCompleteness(profile);
                this.completenessReady = true;
            } catch {
                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.storeLocations = emptyStoreLocations();
                this.syncStoreLocationsComplete();
                this.completenessReady = true;
            } finally {
                if (this.shopId === requestedShopId) {
                    this.loading = false;
                }
            }
        },
        async updateSection(group, sectionId, payload) {
            const shopStore = useShopStore();
            const shopId = this.shopId || shopStore.selectedShop?.id;

            if (group === SELLER_PROFILE_GROUP.StoreInfo) {
                const saved = await persistStoreInfoSection(shopId, sectionId, payload);
                this.storeInfo[sectionId] = saved;
                this.syncStoreInfoComplete();
                this.patchSectionCompleteness({ [sectionId]: saved });
                return saved;
            }

            if (group === SELLER_PROFILE_GROUP.StoreLocations) {
                const saved = await persistStoreLocationsSection(shopId, sectionId, payload);
                return this.applyStoreLocationsSection(saved);
            }

            const saved = await persistPaymentDeliverySection(shopId, sectionId, payload);
            this.paymentDelivery[sectionId] = saved;
            this.syncPaymentDeliveryComplete();
            this.patchSectionCompleteness({ [sectionId]: saved });
            return saved;
        },
        async createStoreLocation(payload, page = 1) {
            const shopStore = useShopStore();
            const shopId = this.shopId || shopStore.selectedShop?.id;
            const saved = await createStoreLocation(shopId, payload, page);

            return this.applyStoreLocationsSection(saved);
        },
        async updateStoreLocation(id, payload, page = 1) {
            const shopStore = useShopStore();
            const shopId = this.shopId || shopStore.selectedShop?.id;
            const saved = await updateStoreLocation(shopId, id, payload, page);

            return this.applyStoreLocationsSection(saved);
        },
        async deleteStoreLocation(id, page = 1) {
            const shopStore = useShopStore();
            const shopId = this.shopId || shopStore.selectedShop?.id;
            const saved = await deleteStoreLocation(shopId, id, page);

            return this.applyStoreLocationsSection(saved);
        },
        async duplicateStoreLocation(id, page = 1) {
            const shopStore = useShopStore();
            const shopId = this.shopId || shopStore.selectedShop?.id;
            const saved = await duplicateStoreLocation(shopId, id, page);

            return this.applyStoreLocationsSection(saved);
        },
    },
});
